/**
 * Authenticated Fetch Utility
 * 
 * Wraps fetch() to automatically include Firebase Auth ID token
 * in the Authorization header for all API requests.
 * 
 * Usage:
 *   import { authenticatedFetch } from '@/utils/authenticatedFetch';
 *   
 *   const data = await authenticatedFetch('/api/projects/');
 *   const project = await authenticatedFetch('/api/projects/save', {
 *     method: 'POST',
 *     body: JSON.stringify(projectData)
 *   });
 */

import { getAuth } from 'firebase/auth';
import { API_BASE_URL } from './apiConfig';


interface AuthenticatedFetchOptions extends RequestInit {
    skipAuth?: boolean;  // For public endpoints
}

/**
 * Fetch with automatic Firebase Auth token injection
 */
async function getAuthToken(skipAuth: boolean): Promise<string | null> {
    if (skipAuth) return null;
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated. Please sign in.');
    try {
        return await user.getIdToken();
    } catch (error) {
        console.error('Failed to get Firebase token:', error);
        throw new Error('Authentication token expired. Please sign in again.');
    }
}

function prepareHeaders(options: AuthenticatedFetchOptions, token: string | null): Headers {
    const headers = new Headers(options.headers || {});
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    } else {
        // Log warning if token is missing but we didn't skip auth
        if (!options.skipAuth) {
            console.warn('⚠️ authenticatedFetch: Token is missing or empty, Authorization header will NOT be set.');
        }
    }

    if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
    }
    return headers;
}

async function handleResponseError(response: Response) {
    if (response.status === 401) {
        console.warn('401 Unauthorized - token may be invalid');
        throw new Error('Authentication failed. Token may be invalid or expired.');
    }
    if (response.status === 403) {
        const errorData = await response.json().catch(() => ({ detail: 'Permission denied' }));
        throw new Error(`Permission denied: ${errorData.detail || 'You do not have access to this resource'}`);
    }
}

/**
 * Fetch with automatic Firebase Auth token injection
 */
export async function authenticatedFetch(
    endpoint: string,
    options: AuthenticatedFetchOptions = {}
): Promise<Response> {
    const { skipAuth = false, ...fetchOptions } = options;

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const token = await getAuthToken(skipAuth || false);
    const headers = prepareHeaders(fetchOptions, token);

    try {
        const response = await fetch(url, { ...fetchOptions, headers });
        if (!response.ok) await handleResponseError(response);
        return response;
    } catch (error) {
        if (error instanceof TypeError && error.message.includes('fetch')) {
            throw new Error(`Network error: Could not reach ${url}. Is the backend running?`);
        }
        throw error;
    }
}

/**
 * Convenience method for JSON responses
 */
export async function authenticatedFetchJSON<T = any>(
    endpoint: string,
    options: AuthenticatedFetchOptions = {}
): Promise<T> {
    const response = await authenticatedFetch(endpoint, options);

    if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
            const errorData = JSON.parse(errorText);
            if (Array.isArray(errorData.detail)) {
                errorMessage = errorData.detail.map((d: any) => `${d.loc.join('.')}: ${d.msg}`).join('; ');
            } else {
                errorMessage = errorData.detail || errorData.message || errorMessage;
            }
        } catch {
            // Not JSON, use text
            if (errorText) errorMessage = errorText;
        }

        throw new Error(errorMessage);
    }

    return await response.json();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    const auth = getAuth();
    return auth.currentUser !== null;
}

/**
 * Get current user's ID token (for debugging)
 */
export async function getCurrentToken(): Promise<string | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) return null;

    try {
        return await user.getIdToken();
    } catch {
        return null;
    }
}

/**
 * Get current user's claims (roles, organization, etc.)
 */
export async function getUserClaims(): Promise<Record<string, unknown> | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) return null;

    try {
        const tokenResult = await user.getIdTokenResult();
        return tokenResult.claims;
    } catch (error) {
        console.error('Failed to get user claims:', error);
        return null;
    }
}
